import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material";

import { SamlAliasView, SamlAliasUpdateRequest, SAML, SamlAliasCreateRequest } from "@impulse/sdp-shared-models";

import { ApiGatewayService } from "../../common/api-gateway/api-gateway.service";
import { TranslateService } from "../../common/translate/translate.service";

@Injectable()
export class SamlAliasesService {
	public readonly PARENT_ROUTE = "saml-alias";

	private _samlAlias!: SamlAliasUpdateRequest;
	private _isCreate = false;
	private samlAliasId = "";


	constructor(
		public dialog: MatDialog,
		private apiGatewayService: ApiGatewayService,
		private translateService: TranslateService
	) { }

	public get samlAlias() {
		return this._samlAlias;
	}

	public get isCreate() {
		return this._isCreate;
	}

	public async deleteSamlAlias(alias: SamlAliasView) {
		await this.apiGatewayService.deleteSamlAlias(alias.id);
		return this.translateService.translate("saml-alias.delete.success", { label: alias.label });
	}

	public async getList() {
		return await this.apiGatewayService.getAllSamlAliases();
	}

	public getValue<T extends keyof SamlAliasUpdateRequest>(key: T, defaultValue: SamlAliasUpdateRequest[T]) {
		const data = this._samlAlias || {};
		return data[key] !== undefined ? data[key] : defaultValue;
	}

	public async processSamlAliasWizardEdit(aliasEdit: SamlAliasView, id: string): Promise<string> {
		// fill in the modified values from the edit
		Object.assign(this._samlAlias, aliasEdit);

		let message = "";

		try {
			// if label is empty populate it with samlGroup, only for create
			if (this._samlAlias.label!.length <= 0) {
				this._samlAlias.label = this._samlAlias.samlGroup;
			}
			if (this.isCreate) {
				// TODO This cast is wrong.  We need a better way to store state.
				await this.apiGatewayService.createSamlAlias(<SamlAliasCreateRequest>this._samlAlias);
				message = this.translateService.translate("saml-alias.add.success", { label: this._samlAlias.label! });
			} else {
				await this.apiGatewayService.updateSamlAlias(this.samlAliasId, this._samlAlias);
				message = this.translateService.translate("saml-alias.update.success", { label: this._samlAlias.label! });
			}
			return message;
		} catch (e) {
			// we dont want to close dialog if an error occurred, pass error back to component for handling
			throw (e);
		}
	}

	public newSamlAlias() {
		this._samlAlias = {
			providerId: "",
			label: "",
			samlGroup: ""
		};
		this._isCreate = true;
	}

	public editSamlAlias(alias: SamlAliasView) {
		this.samlAliasId = alias.id;
		this._samlAlias = {
			providerId: alias.providerId,
			label: alias.label,
			samlGroup: alias.samlGroup
		};
		if (this._samlAlias.samlGroup === this._samlAlias.label) {
			this._samlAlias.label = "";
		}
		this._isCreate = false;
	}

	public async getProviders() {
		return await this.apiGatewayService.getIdentityProviders();
	}

	public getProviderName(row: SamlAliasView, providerData: SAML.IdentityProvider[]) {
		const foundIdp = providerData.find((idp) => {
			return idp.id === row.providerId;
		});
		if (!!foundIdp) {
			return foundIdp.name;
		} else {
			return row.providerId;
		}
	}

	public getProviderNameById(providerData: SAML.IdentityProvider[], id: SAML.IdentityProvider.Id): string {
		const provider = providerData.find((idp) => {
			return idp.id === id;
		});
		return !!provider ? provider.name : id;
	}

	public closeDialog() {
		this.dialog.closeAll();
	}
}
